import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Drawer } from '@mui/material';
import { BasketService } from '@services';
import { useEvent } from '@hooks';
import { StyledTypography } from '@components/drawer/drawer.styles';
import { PriceDisplay } from '@components/price-display';
import { Icon, IconButton, Switch, Typography } from '@elements';
import { colors } from '@themes';
import { StyledButton } from '@popups/place-reserve/place-reserve.styles';
import { BasketRow, RemoveButton, RemoveStack, StyledTypographyLast } from '@views/pages/basket/basket.styles';
import down from '@images/chevron-down.svg';
import CloseIcon from '@images/close.image.svg';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
var drawerBleeding = 99;

var DrawerComponent = function DrawerComponent(_ref) {
  var open = _ref.open,
      toggleDrawer = _ref.toggleDrawer,
      basket = _ref.basket;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  var adultCategoryId = Number(process.env.adultCategoryId);

  var _useEvent = useEvent('onReservedQuantity'),
      dispatchEvent = _useEvent.dispatch;

  var onCategorySwitchClick = function onCategorySwitchClick(basketItem, firstPrice, secondPrice) {
    return function () {
      if (firstPrice && secondPrice) {
        BasketService.changeSeatCategory({
          categoryId: basketItem.categoryId === firstPrice.categoryId ? secondPrice.categoryId : firstPrice.categoryId,
          eventSessionUuid: basketItem.eventSessionUuid,
          locationSchemeSeatId: basketItem.locationSchemeSeatId
        }).then(function (basketResp) {
          BasketService.updateStoreFromResponse(basketResp);
        })["catch"](function () {// error handling
        });
      }
    };
  };

  var onSeatUnreserve = function onSeatUnreserve(item) {
    return function () {
      var _item$eventSessionUui;

      BasketService.unreserveSeat([{
        eventSessionUuid: (_item$eventSessionUui = item === null || item === void 0 ? void 0 : item.eventSessionUuid) !== null && _item$eventSessionUui !== void 0 ? _item$eventSessionUui : '',
        locationSchemeSeats: [{
          categoryId: item === null || item === void 0 ? void 0 : item.categoryId,
          locationSchemeSeatId: item === null || item === void 0 ? void 0 : item.locationSchemeSeatId,
          quantity: 1
        }]
      }]).then(function (basketResp) {
        BasketService.updateStoreFromResponse(basketResp);
        BasketService.externalChangeBasketEvent();
        dispatchEvent({
          data: item,
          diff: -1
        });
      })["catch"](function () {// error handling
      });
    };
  };

  var onSeatReserve = function onSeatReserve(item) {
    return function () {
      var _item$eventSessionUui2;

      BasketService.reserveSeat({
        categoryId: adultCategoryId,
        eventSessionUuid: (_item$eventSessionUui2 = item === null || item === void 0 ? void 0 : item.eventSessionUuid) !== null && _item$eventSessionUui2 !== void 0 ? _item$eventSessionUui2 : '',
        locationSchemeSeatIds: [item === null || item === void 0 ? void 0 : item.locationSchemeSeatId],
        quantity: 1
      }).then(function (basketResp) {
        BasketService.updateStoreFromResponse(basketResp);
        BasketService.externalChangeBasketEvent();
        dispatchEvent({
          data: item,
          diff: 1
        });
      })["catch"](function () {// error handling
      });
    };
  };

  return /*#__PURE__*/_jsx(Box, {
    children: /*#__PURE__*/_jsx(Drawer, {
      anchor: "bottom",
      sx: {
        '& .MuiDrawer-paper': {
          overflow: 'visible',
          height: "calc(50% - ".concat(drawerBleeding, "px)"),
          background: colors.copyCodGray,
          maxWidth: '1192px',
          margin: '0 auto',
          padding: '34px 0 79px 16px',
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12
        }
      },
      open: open,
      onClose: toggleDrawer,
      ModalProps: {
        keepMounted: true
      },
      PaperProps: {},
      BackdropProps: {
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.60)',
          backdropFilter: 'blur(10px)'
        }
      },
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(IconButton, {
          onClick: toggleDrawer,
          sx: {
            position: 'absolute !important',
            top: '-16px',
            left: '50%',
            transform: 'translateX(-50%)'
          },
          children: /*#__PURE__*/_jsx(Icon, {
            src: down,
            width: 14,
            height: 14
          })
        }), /*#__PURE__*/_jsx(Box, {
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          overflow: "auto",
          pr: 2,
          sx: {
            '&::-webkit-scrollbar': {
              width: '4px'
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#9E9E9E',
              borderRadius: '12px'
            }
          },
          children: (basket !== null && basket !== void 0 ? basket : []).map(function (item) {
            var _item$availablePrices, _secondPrice, _secondPrice2;

            var prices = (_item$availablePrices = item.availablePrices) !== null && _item$availablePrices !== void 0 ? _item$availablePrices : [];
            var itemContainAdultCategory = prices.find(function (priceItem) {
              return priceItem.categoryId === adultCategoryId;
            });
            var firstPrice;
            var secondPrice;

            if (prices.length > 1) {
              if (!itemContainAdultCategory) {
                firstPrice = prices[0];
                secondPrice = prices[1];
              } else {
                firstPrice = itemContainAdultCategory;
                secondPrice = prices.filter(function (priceItem) {
                  return priceItem.categoryId !== adultCategoryId;
                })[0];
              }
            }

            return /*#__PURE__*/_jsxs(BasketRow, {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderRadius: "12px",
              width: "100%",
              bgcolor: "rgba(255, 255, 255, 0.03)",
              py: 2,
              px: 2.5,
              children: [/*#__PURE__*/_jsxs(Box, {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "35%",
                children: [/*#__PURE__*/_jsxs(Box, {
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  children: [/*#__PURE__*/_jsxs(Box, {
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    children: [(!!item.locationSchemeSeatsIngroupPrefix || !!item.locationSchemeSeatsIngroupName) && /*#__PURE__*/_jsxs(Box, {
                      display: "flex",
                      gap: 1,
                      children: [!!item.locationSchemeSeatsIngroupPrefix && /*#__PURE__*/_jsx(StyledTypography, {
                        color: "secondarySilver",
                        children: item.locationSchemeSeatsIngroupPrefix
                      }), !!item.locationSchemeSeatsIngroupName && /*#__PURE__*/_jsx(StyledTypography, {
                        color: "white",
                        children: item.locationSchemeSeatsIngroupName
                      })]
                    }), (!!item.locationSchemeSeatPrefix || !!item.locationSchemeSeatName) && /*#__PURE__*/_jsxs(Box, {
                      display: "flex",
                      gap: 1,
                      children: [!!item.locationSchemeSeatPrefix && /*#__PURE__*/_jsx(StyledTypography, {
                        color: "secondarySilver",
                        children: item.locationSchemeSeatPrefix
                      }), !!item.locationSchemeSeatName && /*#__PURE__*/_jsx(StyledTypography, {
                        color: "white",
                        children: item.locationSchemeSeatName
                      })]
                    })]
                  }), /*#__PURE__*/_jsxs(Box, {
                    display: "flex",
                    gap: 1,
                    children: [!!item.locationSchemeSeatsOutgroupPrefix && /*#__PURE__*/_jsx(StyledTypography, {
                      color: "secondarySilver",
                      children: item.locationSchemeSeatsOutgroupPrefix
                    }), !!item.locationSchemeSeatsOutgroupName && /*#__PURE__*/_jsx(StyledTypography, {
                      color: "white",
                      children: item.locationSchemeSeatsOutgroupName
                    })]
                  })]
                }), prices.length > 1 && !item.ga && /*#__PURE__*/_jsxs(Box, {
                  display: "flex",
                  alignItems: "center",
                  gap: 2.25,
                  children: [/*#__PURE__*/_jsx(StyledTypography, {
                    color: "secondarySilver",
                    mt: 0.25,
                    children: (_secondPrice = secondPrice) === null || _secondPrice === void 0 ? void 0 : _secondPrice.categoryName
                  }), /*#__PURE__*/_jsx(Switch, {
                    checked: item.categoryId === ((_secondPrice2 = secondPrice) === null || _secondPrice2 === void 0 ? void 0 : _secondPrice2.categoryId),
                    onClick: onCategorySwitchClick(item, firstPrice, secondPrice)
                  })]
                })]
              }), /*#__PURE__*/_jsxs(Box, {
                children: [/*#__PURE__*/_jsxs(StyledTypographyLast, {
                  color: "white",
                  children: [item.ga && /*#__PURE__*/_jsx(PriceDisplay, {
                    basePrice: item.basePrice,
                    finalPrice: item.finalPrice,
                    quantity: item.quantity,
                    showQuantityPrefix: true
                  }), !item.ga && /*#__PURE__*/_jsx(PriceDisplay, {
                    basePrice: item.basePrice,
                    finalPrice: item.finalPrice,
                    quantity: item.quantity
                  })]
                }), !item.ga && /*#__PURE__*/_jsxs(RemoveButton, {
                  variant: "text",
                  onClick: onSeatUnreserve(item),
                  children: [/*#__PURE__*/_jsx(Icon, {
                    src: CloseIcon,
                    width: 28,
                    height: 28
                  }), /*#__PURE__*/_jsx(Box, {
                    pl: 1,
                    pt: 0.25,
                    children: /*#__PURE__*/_jsx(Typography, {
                      color: "silverChalice",
                      variant: "body1",
                      fontWeight: 500,
                      textTransform: "none",
                      component: "span",
                      display: "flex",
                      children: t('pages.basket.remove')
                    })
                  })]
                }), item.ga && /*#__PURE__*/_jsxs(RemoveStack, {
                  gap: 2,
                  direction: "row",
                  alignItems: "center",
                  children: [/*#__PURE__*/_jsx(StyledButton, {
                    variant: "contained",
                    color: "secondary",
                    size: "medium",
                    disabled: !item.quantity,
                    onClick: onSeatUnreserve(item),
                    children: "-"
                  }), /*#__PURE__*/_jsx(Typography, {
                    variant: "h4",
                    color: "white",
                    fontWeight: 500,
                    children: item.quantity
                  }), /*#__PURE__*/_jsx(StyledButton, {
                    variant: "contained",
                    size: "small",
                    onClick: onSeatReserve(item),
                    children: "+"
                  })]
                })]
              })]
            }, item.locationSchemeSeatId);
          })
        })]
      })
    })
  });
};

export { DrawerComponent };